/**
 * Getting PSBT bytes in, and getting them back out.
 *
 * A PSBT arrives as a `.psbt` file, as base64 pasted from a coordinator, or as hex from a
 * debugging tool. All three are the same five magic bytes underneath, so this normalises to
 * `Uint8Array` once and everything downstream deals in bytes.
 *
 * No network, no `@scure/base` — `atob`/`btoa` are in the platform and this is the audit
 * surface, so fewer imports is the point.
 */

/** BIP-174 magic: "psbt" followed by 0xff. */
const MAGIC = [0x70, 0x73, 0x62, 0x74, 0xff] as const;

/**
 * Invisible formatting characters that a paste carries but `\s` does not match.
 *
 * JS `\s` covers U+FEFF and U+2000–U+200A, and nothing in this range: ZWSP/ZWNJ/ZWJ, the
 * bidi marks, the word joiner, the soft hyphen. Slack, Notion, GitHub comments and mail
 * clients all inject them into text they reflow. The user pasted correct base64, got back
 * "not base64 and not hex. Paste the PSBT as base64" — advice they had already followed —
 * and there was nothing on screen pointing at a character they cannot see.
 *
 * Safe to strip unconditionally: none of them is in the base64 or hex alphabet, so this can
 * only turn an input that was going to be rejected into one that decodes.
 */
const INVISIBLE = /[\u00AD\u200B-\u200F\u2060\uFEFF]/g;

export class InputError extends Error {
  constructor(public readonly code: string, message: string) {
    // The code goes in the message too: it is what a user reads and what a log shows.
    super(`${code}: ${message}`);
    this.name = 'InputError';
  }
}

const hasMagic = (b: Uint8Array): boolean =>
  b.length >= 5 && MAGIC.every((m, i) => b[i] === m);

function fromBase64(s: string): Uint8Array | null {
  try {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch { return null; }
}

function fromHex(s: string): Uint8Array | null {
  if (s.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(s)) return null;
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export const toBase64 = (b: Uint8Array): string => {
  let bin = '';
  // Chunked: String.fromCharCode(...b) blows the argument limit on a large PSBT.
  for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(bin);
};

export const toHex = (b: Uint8Array): string =>
  Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');

/**
 * Normalise anything a user can hand us into PSBT bytes.
 *
 * Fails loudly rather than best-effort. A "nearly a PSBT" blob that we half-parse is exactly
 * how someone ends up reviewing a transaction that is not the one they were sent.
 */
export function decodePsbtInput(input: Uint8Array | string): Uint8Array {
  if (input instanceof Uint8Array) {
    if (hasMagic(input)) return input;
    // A .psbt file saved by a text-mode tool is base64 or hex in a binary wrapper.
    const text = new TextDecoder().decode(input).trim();
    if (text) return decodePsbtInput(text);
    throw new InputError('NOT_A_PSBT', 'file does not start with the PSBT magic bytes');
  }

  const s = input.replace(INVISIBLE, '').replace(/\s+/g, '');
  if (!s) throw new InputError('EMPTY_INPUT', 'nothing to decode');

  for (const decode of [fromBase64, fromHex]) {
    const bytes = decode(s);
    if (bytes && hasMagic(bytes)) return bytes;
  }

  const looksBase64 = /^[A-Za-z0-9+/=]+$/.test(s);
  const looksHex = /^[0-9a-fA-F]+$/.test(s);
  if (looksBase64 || looksHex)
    throw new InputError('NOT_A_PSBT',
      `decoded ${looksHex && !looksBase64 ? 'hex' : 'base64'} cleanly, but the result does not ` +
      `start with the PSBT magic bytes — this is some other blob`);
  throw new InputError('UNRECOGNISED_INPUT',
    'not base64 and not hex. Paste the PSBT as base64, or load the .psbt file itself');
}

/** Hand the user a file. No network involved — this is a blob: URL built in the page. */
export function download(bytes: Uint8Array | string, filename: string, mime: string): void {
  const blob = new Blob([bytes as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately can cancel the download in some browsers; one frame is enough.
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}

export const readFile = (file: File): Promise<Uint8Array> =>
  file.arrayBuffer().then((b) => new Uint8Array(b));
